import { useEffect, useState } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { deleteById, page, save } from "./api-cronograma-quant-aula-dia";
import { findByAnoLetivoESerieCodSerie } from "../../Bimestre/api-bimestre";
import { dateFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import Loading from "../../UI/Loading";
import TurmaDisciplinaProfessorProfile from "../../TDP/TurmaDisciplinaProfessorProfile";
import TDPTableSelect from "../../TDP/TDPTableSelect";
import { mensagemDe } from "../cronogramaUtil";
import CronogramaQuantAulaDiaForm from "./CronogramaQuantAulaDiaForm";
import CronogramaQuantAulaDiaTable from "./CronogramaQuantAulaDiaTable";

const PAGINA_VAZIA = { content: [], size: 0, totalElements: 0, number: 0 };

/** Bimestre em andamento hoje, ou o primeiro da lista. */
const bimestreAtual = (bimestres) => {
  const hoje = moment().format("YYYY-MM-DD");
  return (
    bimestres.find(
      (b) =>
        moment.utc(b.dataInicio).format("YYYY-MM-DD") <= hoje && hoje <= moment.utc(b.dataFim).format("YYYY-MM-DD"),
    ) ?? bimestres[0] ?? null
  );
};

const CronogramaQuantAulaDiaContainer = () => {
  const [tdp, setTdp] = useState(null);
  const [bimestres, setBimestres] = useState([]);
  const [isLoadingBimestres, setIsLoadingBimestres] = useState(false);
  const [bimestre, setBimestre] = useState(null);
  const [list, setList] = useState(PAGINA_VAZIA);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const codTdp = tdp?.codigo;
  const codSerie = tdp?.turma?.serie?.codSerie;
  const codBimestre = bimestre?.codigo;

  useEffect(() => {
    setBimestres([]);
    setBimestre(null);
    if (!codSerie) return;
    let active = true;
    setIsLoadingBimestres(true);
    findByAnoLetivoESerieCodSerie(codSerie)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data, "Não foi possível carregar os bimestres."));
        } else if (Array.isArray(data)) {
          const ordenados = [...data].sort((a, b) => (a.numeroBimestre ?? 0) - (b.numeroBimestre ?? 0));
          setBimestres(ordenados);
          setBimestre(bimestreAtual(ordenados));
        }
      })
      .finally(() => {
        if (active) setIsLoadingBimestres(false);
      });
    return () => {
      active = false;
    };
  }, [codSerie]);

  useEffect(() => {
    if (!codTdp || !codBimestre) {
      setList(PAGINA_VAZIA);
      return;
    }
    let active = true;
    setIsLoading(true);
    page(pageNumber, codTdp, codBimestre)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data, "Não foi possível carregar as quantidades de aulas."));
        } else {
          setList({ ...PAGINA_VAZIA, ...data });
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codTdp, codBimestre, pageNumber, reload]);

  const handleTdpSelected = (novo) => {
    setTdp(novo);
    setPageNumber(0);
    setEditando(null);
  };

  const handleBimestreSelected = (novo) => {
    setBimestre(novo);
    setPageNumber(0);
  };

  const handleSave = (persist) => {
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível salvar a quantidade de aulas."));
          return;
        }
        setEditando(null);
        success("Registro salvo com sucesso.");
        setReload((n) => n + 1);
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = (item) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a quantidade de aulas do dia ${dateFormat(item.data)}?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            deleteById(item.codigo).then((data) => {
              if (data?.error) {
                errorMessage(mensagemDe(data, "Não foi possível remover a quantidade de aulas."));
                return;
              }
              success("Registro removido.");
              // Volta uma página se a atual ficou vazia.
              if (list.content.length === 1 && pageNumber > 0) {
                setPageNumber((n) => n - 1);
              } else {
                setReload((n) => n + 1);
              }
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  return (
    <Container>
      <CronogramaQuantAulaDiaForm
        cqad={editando}
        bimestre={bimestre}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Box mb={3}>
        <Typography variant="h6">Quantidade de Aulas por Dia</Typography>
        <Typography variant="body2" color="text.secondary">
          Quantas aulas a turma - disciplina tem em cada dia do bimestre.
        </Typography>
      </Box>

      {!tdp ? (
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Selecione uma turma - disciplina
          </Typography>
          <TDPTableSelect setTdp={handleTdpSelected} />
        </Box>
      ) : (
        <Stack spacing={3}>
          <TurmaDisciplinaProfessorProfile tdp={tdp} onTdpSelected={handleTdpSelected} />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              Bimestre
            </Typography>
            {isLoadingBimestres ? (
              <Loading description="Carregando bimestres..." />
            ) : bimestres.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum bimestre cadastrado para a série desta turma.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {bimestres.map((b) => (
                  <Chip
                    key={b.codigo}
                    label={`${b.numeroBimestre}º Bimestre · ${dateFormat(b.dataInicio)} a ${dateFormat(b.dataFim)}`}
                    color={b.codigo === codBimestre ? "primary" : "default"}
                    variant={b.codigo === codBimestre ? "filled" : "outlined"}
                    onClick={() => handleBimestreSelected(b)}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {bimestre && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Dias do {bimestre.numeroBimestre}º bimestre
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setEditando({ eNovo: true, tdp, bimestre })}
                >
                  Novo Registro
                </Button>
              </Stack>
              <CronogramaQuantAulaDiaTable
                list={list}
                isLoading={isLoading}
                onChangePage={(event, newPage) => setPageNumber(newPage)}
                onEdit={setEditando}
                onDelete={handleDelete}
              />
            </Box>
          )}
        </Stack>
      )}
    </Container>
  );
};

export default CronogramaQuantAulaDiaContainer;
